import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  ConfigProvider, Layout, Card, Avatar, Button, Input,
  List, Upload, message, Dropdown, Space, Typography,
  Tag, Tooltip, Divider, theme
} from 'antd';
import {
  Heart, MessageCircle, MoreHorizontal, FileText,
  Image as ImageIcon, Video, X, Send, Share2,
  Trash2, Edit2, Paperclip, ThumbsUp
} from 'lucide-react';
import { AuthContext } from '../context/auth/AuthContext';
import { postApi } from '../api/postApi';

// Load Rubik Font
const FontLoader = () => (
  <style>
    {`
      @import url('https://fonts.googleapis.com/css2?family=Rubik:ital,wght@0,300..900;1,300..900&display=swap');
      body, .ant-typography, .ant-btn, .ant-input, .ant-menu {
        font-family: 'Rubik', sans-serif !important;
      }
    `}
  </style>
);

const { Header, Content } = Layout;
const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

// --- Mock Data (Preserved) ---
const SAMPLE_POSTS = [
  {
    _id: 'sample-1',
    user: { _id: 'user-1', name: 'AID Organization', avatar: null },
    content: 'Welcome to AID Social Media! This is a sample post to demonstrate the platform. Share your thoughts, projects, and connect with the community.',
    media: [
      { mediaType: 'image', url: 'https://media.istockphoto.com/id/2193545991/photo/senior-manager-shaking-hand-with-a-young-woman-team-member-during-a-conference-meeting.jpg?s=2048x2048&w=is&k=20&c=kKIMBxGu9DEwqEEtQQ96oSR4vQ1oBlrnqkcVxNSgMk8=', originalName: 'team-collaboration.jpg' }
    ],
    likes: ['user-2', 'user-3'],
    comments: [
      { _id: 'comment-1', user: { _id: 'user-2', name: 'John Doe' }, content: 'Great initiative! Looking forward to connecting with everyone.', createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() }
    ],
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: 'sample-2',
    user: { _id: 'user-1', name: 'AID Organization', avatar: null },
    content: 'Check out our latest industrial development report. This comprehensive document covers key insights and trends in the Vidarbha region.',
    media: [
      { mediaType: 'document', url: '#', originalName: 'Industrial_Development_Report_2024.pdf', thumbnail: null }
    ],
    likes: ['user-2', 'user-3', 'user-4'],
    comments: [
      { _id: 'comment-2', user: { _id: 'user-3', name: 'Jane Smith' }, content: 'Very informative report!', createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString() }
    ],
    createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: 'sample-3',
    user: { _id: 'user-5', name: 'Rajesh Kumar', avatar: null },
    content: 'Excited to announce that our manufacturing unit has successfully implemented green energy solutions! Reduced our carbon footprint by 40% in the last quarter. Sustainability is the future of industry! 🌱',
    media: [
      { mediaType: 'image', url: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800', originalName: 'solar-panels.jpg' }
    ],
    likes: ['user-1', 'user-2', 'user-3', 'user-6', 'user-7'],
    comments: [
      { _id: 'comment-3', user: { _id: 'user-2', name: 'John Doe' }, content: 'Congratulations! This is inspiring for all of us.', createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString() },
      { _id: 'comment-4', user: { _id: 'user-6', name: 'Priya Sharma' }, content: 'Would love to know more about the implementation process!', createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() }
    ],
    createdAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: 'sample-4',
    user: { _id: 'user-8', name: 'TechStart Industries', avatar: null },
    content: 'We are hiring! Looking for experienced Mechanical Engineers and Production Supervisors for our new facility in Nagpur. Competitive salary and excellent growth opportunities. DM for details or visit our careers page.',
    media: [],
    likes: ['user-2', 'user-5', 'user-9'],
    comments: [
      { _id: 'comment-5', user: { _id: 'user-9', name: 'Amit Patel' }, content: 'Shared with my network. Great opportunity!', createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString() }
    ],
    createdAt: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: 'sample-5',
    user: { _id: 'user-10', name: 'Sneha Deshmukh', avatar: null },
    content: 'Just attended an amazing workshop on Industry 4.0 and IoT integration. The future of manufacturing is here! Special thanks to AID for organizing such insightful sessions. Here are some key takeaways from the event.',
    media: [
      { mediaType: 'image', url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800', originalName: 'workshop.jpg' }
    ],
    likes: ['user-1', 'user-3', 'user-5', 'user-8'],
    comments: [
      { _id: 'comment-6', user: { _id: 'user-1', name: 'AID Organization' }, content: 'Thank you for attending! Stay tuned for more events.', createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString() },
      { _id: 'comment-7', user: { _id: 'user-11', name: 'Vikram Singh' }, content: 'Missed this one. Hope recordings are available!', createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString() }
    ],
    createdAt: new Date(Date.now() - 60 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: 'sample-6',
    user: { _id: 'user-12', name: 'Maharashtra Industries', avatar: null },
    content: 'New government policy updates for MSME sector - simplified GST compliance and enhanced credit access. This is a game-changer for small and medium enterprises!',
    media: [
      { mediaType: 'document', url: '#', originalName: 'MSME_Policy_Update_Dec2024.pdf', thumbnail: null }
    ],
    likes: ['user-1', 'user-2', 'user-5', 'user-8', 'user-10', 'user-13'],
    comments: [
      { _id: 'comment-8', user: { _id: 'user-13', name: 'Ravi Mehta' }, content: 'Finally! This will help so many businesses.', createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString() },
      { _id: 'comment-9', user: { _id: 'user-5', name: 'Rajesh Kumar' }, content: 'Already processing our application. Thanks for sharing!', createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString() }
    ],
    createdAt: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: 'sample-7',
    user: { _id: 'user-14', name: 'Anita Kulkarni', avatar: null },
    content: 'Proud to share that our startup has been selected for the AID Incubation Program! Looking forward to mentorship, networking, and scaling our agri-tech solution. Dreams do come true with the right support! 🚀',
    media: [
      { mediaType: 'image', url: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800', originalName: 'startup-celebration.jpg' }
    ],
    likes: ['user-1', 'user-2', 'user-3', 'user-5', 'user-8', 'user-10', 'user-12'],
    comments: [
      { _id: 'comment-10', user: { _id: 'user-1', name: 'AID Organization' }, content: 'Congratulations Anita! We are excited to work with you.', createdAt: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString() },
      { _id: 'comment-11', user: { _id: 'user-10', name: 'Sneha Deshmukh' }, content: 'Well deserved! Your pitch was outstanding.', createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString() },
      { _id: 'comment-12', user: { _id: 'user-15', name: 'Karan Joshi' }, content: 'Congratulations! Let us know if you need any tech support.', createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString() }
    ],
    createdAt: new Date(Date.now() - 84 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: 'sample-8',
    user: { _id: 'user-16', name: 'Industrial Safety Council', avatar: null },
    content: 'Reminder: Workplace safety training session scheduled for next week. Topics include fire safety, equipment handling, and emergency response protocols. All members are encouraged to participate. Registration link in bio.',
    media: [],
    likes: ['user-2', 'user-5', 'user-8', 'user-12'],
    comments: [],
    createdAt: new Date(Date.now() - 96 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: 'sample-9',
    user: { _id: 'user-17', name: 'Suresh Rao', avatar: null },
    content: 'Our textile unit completed ISO 9001:2015 certification! A proud moment for the entire team. Quality and continuous improvement are at the heart of everything we do. Thank you AID for the guidance throughout this journey.',
    media: [
      { mediaType: 'image', url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800', originalName: 'certification.jpg' }
    ],
    likes: ['user-1', 'user-3', 'user-5', 'user-8', 'user-10', 'user-12', 'user-14'],
    comments: [
      { _id: 'comment-13', user: { _id: 'user-1', name: 'AID Organization' }, content: 'Congratulations Suresh! Your dedication is commendable.', createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString() },
      { _id: 'comment-14', user: { _id: 'user-12', name: 'Maharashtra Industries' }, content: 'Well done! This sets a great example.', createdAt: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString() }
    ],
    createdAt: new Date(Date.now() - 108 * 60 * 60 * 1000).toISOString()
  },
  {
    _id: 'sample-10',
    user: { _id: 'user-18', name: 'Tech Innovators Hub', avatar: null },
    content: 'Exciting announcement! We are launching a series of webinars on Digital Transformation in Manufacturing. First session covers Cloud Computing and Data Analytics. Free for all AID members. Register now!',
    media: [
      { mediaType: 'image', url: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800', originalName: 'webinar.jpg' }
    ],
    likes: ['user-1', 'user-2', 'user-5', 'user-10', 'user-14', 'user-17'],
    comments: [
      { _id: 'comment-15', user: { _id: 'user-10', name: 'Sneha Deshmukh' }, content: 'Perfect timing! Registered already.', createdAt: new Date(Date.now() - 9 * 60 * 60 * 1000).toISOString() },
      { _id: 'comment-16', user: { _id: 'user-19', name: 'Deepak Agarwal' }, content: 'Will the sessions be recorded?', createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString() },
      { _id: 'comment-17', user: { _id: 'user-18', name: 'Tech Innovators Hub' }, content: 'Yes, all sessions will be recorded and available to registered members.', createdAt: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString() }
    ],
    createdAt: new Date(Date.now() - 120 * 60 * 60 * 1000).toISOString()
  },
];

// --- Helpers ---
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
};

const getAvatarColor = (name) => {
  const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae'];
  return colors[name.charCodeAt(0) % colors.length];
};

// --- Sub-Components ---

const Post = ({ post, onLike, onComment, onEdit, onDelete, currentUser }) => {
  const [liked, setLiked] = useState(post.likes.includes(currentUser?._id));
  const [likesCount, setLikesCount] = useState(post.likes.length);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState(post.comments || []);
  const [submitting, setSubmitting] = useState(false);

  // AntD Theme Tokens
  const { token } = theme.useToken();

  const handleLike = async () => {
    if (!currentUser) return message.warning('Please login to like posts');
    try {
      const result = await onLike(post._id);
      setLiked(result.isLiked);
      setLikesCount(result.likes);
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleComment = async () => {
    if (!commentText.trim() || !currentUser) return;
    try {
      setSubmitting(true);
      await onComment(post._id, { content: commentText });
      setCommentText('');
      const updatedComments = await postApi.getComments(post._id);
      setComments(updatedComments.comments);
      message.success('Comment added');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderMedia = () => {
    if (!post.media || post.media.length === 0) return null;

    return (
      <div style={{ marginTop: 16, borderRadius: 8, overflow: 'hidden' }}>
        {post.media.map((media, index) => {
          if (media.mediaType === 'image') {
            return <img key={index} src={media.url} alt="post" style={{ width: '100%', objectFit: 'cover' }} />;
          }
          if (media.mediaType === 'video') {
            return (
              <video key={index} controls style={{ width: '100%' }}>
                <source src={media.url} type="video/mp4" />
              </video>
            );
          }
          if (media.mediaType === 'document') {
            return (
              <Card key={index} size="small" style={{ background: '#f9f9f9', borderColor: '#f0f0f0' }}>
                <Space>
                  <FileText color={token.colorPrimary} />
                  <Text strong>{media.originalName}</Text>
                  <Button type="link" href={media.url} target="_blank" style={{ color: '#FF8F00' }}>
                    View PDF
                  </Button>
                </Space>
              </Card>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const canEdit = currentUser && (currentUser._id === post.user._id || currentUser.role === 'admin');

  const menuItems = [
    { key: 'edit', label: 'Edit', icon: <Edit2 size={16} />, onClick: () => onEdit(post) },
    { key: 'delete', label: 'Delete', icon: <Trash2 size={16} />, danger: true, onClick: () => onDelete(post._id) },
  ];

  return (
    <Card
      style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', borderColor: '#f0f0f0' }}
      bodyStyle={{ padding: '24px 24px 12px 24px' }}
      actions={[
        <Button
          type="text"
          block
          icon={<Heart size={20} fill={liked ? '#FF8F00' : 'none'} color={liked ? '#FF8F00' : '#666'} />}
          onClick={handleLike}
          style={{ height: 45, color: liked ? '#FF8F00' : undefined }}
        >
          <span style={{ marginLeft: 8 }}>{likesCount} Likes</span>
        </Button>,
        <Button
          type="text"
          block
          icon={<MessageCircle size={20} />}
          onClick={() => setShowComments(!showComments)}
          style={{ height: 45 }}
        >
          <span style={{ marginLeft: 8 }}>{comments.length} Comments</span>
        </Button>,
        <Button type="text" block icon={<Share2 size={20} />} style={{ height: 45 }}>
          <span style={{ marginLeft: 8 }}>Share</span>
        </Button>
      ]}
    >
      <Card.Meta
        avatar={
          <Avatar
            size={48}
            style={{ backgroundColor: getAvatarColor(post.user.name), verticalAlign: 'middle' }}
          >
            {post.user.name.charAt(0)}
          </Avatar>
        }
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text style={{ fontSize: 18, fontWeight: 500, color: '#3C007A' }}>{post.user.name}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 14, fontWeight: 400 }}>{formatDate(post.createdAt)}</Text>
            </div>
            {canEdit && (
              <Dropdown menu={{ items: menuItems }} placement="bottomRight">
                <Button type="text" icon={<MoreHorizontal />} />
              </Dropdown>
            )}
          </div>
        }
      />

      <div style={{ marginTop: 16 }}>
        <Paragraph style={{ fontSize: 16, fontWeight: 400, color: '#333' }}>
          {post.content}
        </Paragraph>
        {renderMedia()}
      </div>

      {showComments && (
        <div style={{ marginTop: 24, background: '#fafafa', padding: 16, borderRadius: 8 }}>
          <List
            dataSource={comments}
            itemLayout="horizontal"
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar size="small" style={{ backgroundColor: getAvatarColor(item.user.name) }}>{item.user.name.charAt(0)}</Avatar>}
                  title={<Text strong style={{ color: '#3C007A' }}>{item.user.name}</Text>}
                  description={<Text style={{ fontSize: 15 }}>{item.content}</Text>}
                />
              </List.Item>
            )}
          />
          {currentUser && (
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <Avatar src={currentUser.avatar} style={{ backgroundColor: '#3C007A' }}>{currentUser.name?.charAt(0)}</Avatar>
              <div style={{ flex: 1 }}>
                <Input
                  placeholder="Write a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onPressEnter={handleComment}
                  suffix={
                    <Button
                      type="text"
                      icon={<Send size={16} color="#3C007A" />}
                      onClick={handleComment}
                      loading={submitting}
                    />
                  }
                  style={{ borderRadius: 20 }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const CreatePostForm = ({ onSubmit, onCancel, user }) => {
  const [content, setContent] = useState('');
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleUploadChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const handleSubmit = async () => {
    if (!content.trim() && fileList.length === 0) return;
    setLoading(true);
    try {
      // Mocking file object conversion for the API wrapper
      const mediaFiles = fileList.map(f => f.originFileObj);
      await onSubmit({ content, media: mediaFiles });
      setContent('');
      setFileList([]);
      onCancel();
      message.success('Post created successfully!');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title="Create Post"
      style={{ marginBottom: 24, borderRadius: 12, borderColor: '#3C007A' }}
      headStyle={{ borderBottom: '1px solid #eee', color: '#3C007A', fontSize: 18 }}
    >
      <Space align="start" style={{ width: '100%', marginBottom: 16 }}>
        <Avatar size="large" style={{ backgroundColor: '#3C007A' }}>{user?.name?.charAt(0)}</Avatar>
        <TextArea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={`What's on your mind, ${user?.name}?`}
          autoSize={{ minRows: 3, maxRows: 6 }}
          style={{ fontSize: 16, border: 'none', background: 'transparent', resize: 'none' }}
          bordered={false}
        />
      </Space>

      <Upload
        listType="picture-card"
        fileList={fileList}
        onChange={handleUploadChange}
        beforeUpload={() => false} // Prevent auto upload
      >
        {fileList.length >= 4 ? null : (
          <div>
            <ImageIcon size={20} color="#666" />
            <div style={{ marginTop: 8, fontSize: 12 }}>Add Media</div>
          </div>
        )}
      </Upload>

      <Divider style={{ margin: '12px 0' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space size="large">
          <Tooltip title="Add Photo"><Button type="text" icon={<ImageIcon color="#FF8F00" />} /></Tooltip>
          <Tooltip title="Add Video"><Button type="text" icon={<Video color="#FF8F00" />} /></Tooltip>
          <Tooltip title="Add Document"><Button type="text" icon={<Paperclip color="#FF8F00" />} /></Tooltip>
        </Space>

        <Space>
          <Button onClick={onCancel} style={{ borderRadius: 20 }}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={!content.trim() && fileList.length === 0}
            style={{ borderRadius: 20, paddingLeft: 24, paddingRight: 24, fontWeight: 500 }}
          >
            Post
          </Button>
        </Space>
      </div>
    </Card>
  );
};

const SocialMediaPage = () => {
  const { user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [useSampleData, setUseSampleData] = useState(false);

  // Theme configuration for the specific request
  const customTheme = {
    token: {
      fontFamily: 'Rubik, sans-serif',
      colorPrimary: '#3C007A', // Deep Purple
      colorLink: '#FF8F00',    // Amber/Orange for links
      colorTextBase: '#222222',
      fontSize: 16,            // Increased font size
      borderRadius: 8,
      fontWeightGlobal: 400,   // Forced weight 400
    },
    components: {
      Button: {
        fontWeight: 400,
        controlHeight: 40,
      },
      Card: {
        headerFontSize: 18,
        headerFontWeight: 400, // Ensuring headers aren't too bold
      }
    }
  };

  useEffect(() => { loadPosts(); }, []);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const response = await postApi.getPosts({ page: 1, limit: 10 });
      if (response.posts?.length > 0) {
        setPosts(response.posts);
      } else {
        setUseSampleData(true);
        setPosts(SAMPLE_POSTS);
      }
    } catch (e) {
      setUseSampleData(true);
      setPosts(SAMPLE_POSTS);
    } finally {
      setLoading(false);
    }
  };

  // Simplified handlers for the demo
  const handleCreatePost = async (postData) => {
    const newPost = {
      _id: `new-${Date.now()}`,
      user: { _id: user?._id || 'guest', name: user?.name || 'Guest User' },
      content: postData.content,
      media: [],
      likes: [],
      comments: [],
      createdAt: new Date().toISOString()
    };
    setPosts([newPost, ...posts]);
    setShowCreateForm(false);
  };

  return (
    <ConfigProvider theme={customTheme}>
      <FontLoader />
      <Layout style={{ minHeight: '100vh', background: '#f4f6f8' }}>
        <h2 style={{ fontSize: "43px", fontWeight: "600", marginBottom: "24px", color: "#470172", letterSpacing: "-0.5px", marginTop: "80px", textAlign: "center" }}>
          AID-Social: Connect, Share, and Grow Together
        </h2>
        <p style={{ maxWidth: "850px", margin: "0 auto 60px", color: "#666", lineHeight: "1.8", fontSize: "18px", fontWeight: "400" }}>
          Empowering the industrial community through connection. Discover opportunities, discuss policies, and grow your network.        </p>



        <Content style={{ padding: '24px', maxWidth: 800, margin: '0 auto', width: '100%' }}>


          {!showCreateForm && user && (
            <Card style={{ marginBottom: 24, borderRadius: 12, cursor: 'pointer' }} onClick={() => setShowCreateForm(true)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Avatar size="large" style={{ backgroundColor: '#3C007A' }}>{user.name.charAt(0)}</Avatar>
                <div style={{
                  flex: 1,
                  background: '#f5f5f5',
                  borderRadius: 24,
                  padding: '12px 20px',
                  color: '#666',
                  border: '1px solid #eee'
                }}>
                  Start a post, {user.name}...
                </div>
              </div>
            </Card>
          )}

          {showCreateForm && (
            <CreatePostForm
              user={user}
              onSubmit={handleCreatePost}
              onCancel={() => setShowCreateForm(false)}
            />
          )}

          <div className="feed-container">
            {posts.map(post => (
              <Post
                key={post._id}
                post={post}
                onLike={async () => ({ isLiked: !post.likes.includes(user?._id), likes: post.likes.length + 1 })} // Mock return
                onComment={async () => { }} // Mock return
                onEdit={() => { }}
                onDelete={() => { }}
                currentUser={user}
              />
            ))}
          </div>

          {loading && <div style={{ textAlign: 'center', padding: 20 }}>Loading...</div>}
          {!loading && posts.length > 0 && (
            <Divider style={{ color: '#999', borderColor: '#e0e0e0' }}>You have reached the end</Divider>
          )}
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

export default SocialMediaPage;